import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useForm } from 'react-hook-form'
import {
  MailIcon,
  LockIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeOffIcon
} from 'lucide-react'
import Button from '@components/ui/Button'
import BrandLogo from '@components/BrandLogo'
import Input from '@components/ui/Input'

// Google brand icon SVG (official colors)
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

const Login = () => {
  /**
   * CRITICAL: React Hooks Order Guarantee
   * All hooks MUST be called before any conditional returns to maintain consistent hook order.
   * This prevents "Rendered fewer hooks than expected" errors.
   * DO NOT add conditional returns or early exits before ALL hooks are initialized.
   */

  // 1. Call ALL hooks first - no conditionals allowed before this block
  const { signIn, signInWithGoogle, user, profile, photographerProfile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  // Ric Okoniewski (Nautica, 2 Oct 2026): approved talent get a login with no password until they set one from the
  // approval email; when that link expired, "Invalid email or password" was a dead end. Say what to do instead.
  const [failedEmail, setFailedEmail] = useState(null)
  const [rememberMe, setRememberMe] = useState(false)

  // IMPORTANT: useForm() must be called BEFORE any conditional returns
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  // 2. Now safe to have computed values and effects

  // Redirect if already logged in (role-aware for videographers)
  useEffect(() => {
    // Only redirect if we have a user AND profile loaded
    if (user && profile) {
      console.log('User is already logged in, redirecting...')
      if (profile.role === 'photographer') {
        // Check if videographer and route accordingly
        if (photographerProfile?.is_videographer) {
          console.log('[Login] Routing videographer to videographer dashboard')
          navigate('/talent/dashboard/videographer')
        } else {
          console.log('[Login] Routing photographer to photographer dashboard')
          navigate('/talent/dashboard')
        }
      } else {
        navigate('/dashboard')
      }
    }
  }, [user, profile, photographerProfile, navigate])

  // 3. Conditional returns are now safe - all hooks have been called
  // Show spinner only while checking auth AND user exists
  // This prevents infinite spinner for non-logged-in users
  if (authLoading && user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div role="status" aria-label="Checking your session" className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  const onSubmit = async (data) => {
    setLoading(true)

    try {
      const result = await signIn(data.email, data.password)

      // The signIn function handles navigation and returns result
      if (!result.success) {
        console.error('Login failed:', result.error)
        if (/invalid email or password/i.test(String(result.error || ''))) setFailedEmail(data.email)
        // Only set loading to false if login failed
        setLoading(false)
      }
      // If successful, don't set loading to false since we're navigating away
    } catch (error) {
      console.error('Login error:', error)
      // Only set loading to false on error
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <Link to="/">
                <BrandLogo size="lg" variant="icon" showText={false} />
              </Link>
            </div>
            <h2 className="text-3xl font-display font-bold text-foreground">
              Welcome back
            </h2>
            <p className="mt-2 text-muted-foreground">
              Sign in to continue to your account
            </p>
          </div>

          {failedEmail && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status">
              <p className="font-medium">New to Love &amp; Photos talent?</p>
              <p className="mt-1">When you&apos;re approved, you choose your password from the link in your approval email. If you haven&apos;t done that yet, or the link has expired, we can send you a new one.</p>
              <Link to={`/forgot-password?email=${encodeURIComponent(failedEmail)}`} className="mt-2 inline-block font-semibold text-primary hover:underline">Send me a link to set my password</Link>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-4">
              <Input
                label="Email Address"
                type="email"
                icon={<MailIcon className="w-5 h-5 text-muted-foreground" />}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={errors.email?.message}
                placeholder="you@example.com"
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  icon={<LockIcon className="w-5 h-5 text-muted-foreground" />}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  error={errors.password?.message}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-muted-foreground">
                  Remember me
                </label>
              </div>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              loading={loading}
              className="w-full"
              size="lg"
            >
              Sign In
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-background px-3 text-muted-foreground">or continue with</span>
            </div>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={() => signInWithGoogle()}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border rounded-lg bg-card hover:bg-muted transition-colors text-sm font-medium text-foreground"
          >
            <GoogleIcon />
            Sign in with Google
          </button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex lg:flex-1 relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1200"
            alt="Wedding photography"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dusty-900/60 to-transparent" />
        </div>
      </div>
    </div>
  )
}

export default Login
